import { useEffect, useRef, useState } from 'react';
import { CamOffIcon } from './Icons.tsx';

interface Props {
  stream: MediaStream | null;
  mirror: boolean;
  camOn: boolean;
}

/** Draggable self-view. Snaps to the nearest corner when released. */
export function LocalPip({ stream, mirror, camOn }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [corner, setCorner] = useState<'tl' | 'tr' | 'bl' | 'br'>('tr');
  const [drag, setDrag] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video && video.srcObject !== stream) video.srcObject = stream;
  }, [stream]);

  const onPointerDown = (event: React.PointerEvent) => {
    boxRef.current?.setPointerCapture(event.pointerId);
    setDrag({ x: event.clientX, y: event.clientY });
  };

  const onPointerMove = (event: React.PointerEvent) => {
    if (!drag || !boxRef.current) return;
    boxRef.current.style.transform = `translate(${event.clientX - drag.x}px, ${
      event.clientY - drag.y
    }px)`;
  };

  const onPointerUp = (event: React.PointerEvent) => {
    if (!drag || !boxRef.current) return;
    boxRef.current.style.transform = '';
    setDrag(null);

    const rect = boxRef.current.getBoundingClientRect();
    const centreX = rect.left + rect.width / 2 + (event.clientX - drag.x);
    const centreY = rect.top + rect.height / 2 + (event.clientY - drag.y);
    setCorner(
      `${centreY < window.innerHeight / 2 ? 't' : 'b'}${
        centreX < window.innerWidth / 2 ? 'l' : 'r'
      }` as typeof corner,
    );
  };

  const position = {
    tl: 'top-4 left-4',
    tr: 'top-4 right-4',
    bl: 'bottom-28 left-4',
    br: 'bottom-28 right-4',
  }[corner];

  return (
    <div
      ref={boxRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      className={`absolute z-20 w-28 cursor-grab touch-none overflow-hidden rounded-2xl border border-white/15 bg-ink-900 shadow-2xl transition-[top,left,right,bottom] duration-300 active:cursor-grabbing sm:w-44 ${position} ${
        drag ? 'scale-105' : ''
      }`}
      style={{ top: corner.startsWith('t') ? 'max(1rem, env(safe-area-inset-top))' : undefined }}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={`aspect-[3/4] w-full object-cover sm:aspect-video ${mirror ? 'mirrored' : ''} ${
          camOn ? '' : 'opacity-0'
        }`}
      />
      {!camOn && (
        <span className="absolute inset-0 flex items-center justify-center text-white/40">
          <CamOffIcon width={24} height={24} />
        </span>
      )}
      <span className="pointer-events-none absolute bottom-1.5 left-2 text-[10px] font-medium uppercase tracking-widest text-white/60">
        you
      </span>
    </div>
  );
}
